import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useParams, useNavigate } from 'react-router-dom'
import type { Playlist } from 'types/playlist'
import type { State } from 'types/redux'
import {
  loadPlaylist,
  updatePlaylist,
} from '@disco/state-management/actions/playlist-actions'
import { getPlaylist } from '@disco/state-management/selectors/playlists'
import AlbumGridItem from 'components/common/album-grid-item'
import Row from 'components/common/row'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ChoiceList from 'components/ui/choice-list'
import ColorInput from 'components/ui/color-input'
import DatePicker from 'components/ui/date-picker'
import FileSelector from 'components/ui/file-selector'
import Grid from 'components/ui/grid'
import Label from 'components/ui/label'
import SidebarModal from 'components/ui/sidebar-modal'

const getSelectedView = (album: Playlist) => {
  if (album['color']) {
    return 'color'
  }

  if (album['album_image_url']) {
    return 'image'
  }

  return 'default'
}

const LibrariesLibraryAlbumsAlbum = (): React.ReactNode => {
  const params = useParams<{ albumId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const albumId: number = parseInt(params.albumId!, 10)
  const album: Playlist = useSelector((state: State) =>
    getPlaylist(state, {
      id: albumId,
    }),
  )
  useEffect(() => {
    dispatch(loadPlaylist(albumId))
  }, [albumId])
  const [selectedView, setSelectedView] = useState(getSelectedView(album))
  const [color, setColor] = useState(
    album && album['color'] ? album['color'] : '#00D084',
  )
  const [image, setImage] = useState<File | null | undefined>(null)
  const [imageUrl, setImageUrl] = useState<string | null | undefined>(null)
  const [releaseDate, setReleaseDate] = useState('')
  useEffect(() => {
    if (album['release_date']) {
      setReleaseDate(album['release_date'])
    }
  }, [album])

  const handleImageChange = (files: File[] | null | undefined) => {
    const file = files ? files[0] : null
    setImage(file)
  }

  const handleCancel = () => navigate(-1)

  const handleSubmit = (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    dispatch(
      updatePlaylist(albumId, {
        album_image: ['default', 'color'].includes(selectedView) ? null : image,
        color: ['default', 'image'].includes(selectedView) ? null : color,
        release_date: releaseDate === '' ? null : releaseDate,
      }),
    )
    navigate(-1)
  }

  const renderContent = () => {
    if (selectedView === 'default') {
      return (
        <AlbumGridItem
          album={{ ...album, release_date: releaseDate }}
          color={null}
          image={album['tracks_album_image']}
        />
      )
    }

    if (selectedView === 'color') {
      return (
        <>
          <Row columnsSize={[12]}>
            <div>
              <Label caption="Select a color for your album cover" />
              <ColorInput
                name="style.color"
                value={color}
                onChange={color => setColor(color.hex)}
              />
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <div>
              <AlbumGridItem
                album={{ ...album, release_date: releaseDate }}
                color={color}
                image={null}
              />
            </div>
          </Row>
        </>
      )
    }

    if (selectedView === 'image') {
      return (
        <>
          <Row columnsSize={[12]}>
            <div>
              <Label
                caption={
                  <>
                    Select an image for your album cover
                    <br />
                    (recommended size: 750 x 750px)
                  </>
                }
              />
              <FileSelector
                caption={[
                  'Drag new or existing images here, or click to browse your computer',
                  'Click to change image',
                ]}
                image={album && album['album_image_url']}
                showPreview
                type="image"
                onDrop={handleImageChange}
                onPreview={setImageUrl}
                height={132}
                dnd
              />
            </div>
          </Row>
          <Row columnsSize={[12]}>
            <div>
              <AlbumGridItem
                album={{ ...album, release_date: releaseDate }}
                color={null}
                image={imageUrl || (album && album['album_image_url']) || ''}
              />
            </div>
          </Row>
        </>
      )
    }

    return null
  }

  return (
    <SidebarModal
      title="Album options"
      sidebar={
        <>
          <Row columnsSize={[12]}>
            <div>
              <ChoiceList
                title="Choose design view for album"
                multiple={false}
                items={[
                  {
                    label: 'Default',
                    value: 'default',
                  },
                  {
                    label: 'Choose a color',
                    value: 'color',
                  },
                  {
                    label: 'Choose an image',
                    value: 'image',
                  },
                ]}
                selected={selectedView}
                onChange={(value: string) => setSelectedView(value)}
              />
            </div>
          </Row>
          <div
            style={{
              minHeight: 300,
            }}
          >
            <Label caption="Release date" />
            <DatePicker value={releaseDate} onChange={setReleaseDate} />
          </div>
        </>
      }
      content={<Grid size="medium">{renderContent()}</Grid>}
      actions={
        <ButtonContainer>
          <Button appearance="link" caption="Cancel" onClick={handleCancel} />
          <Button
            appearance="primary"
            caption="Confirm"
            onClick={handleSubmit}
          />
        </ButtonContainer>
      }
      onClose={handleCancel}
    />
  )
}

export default LibrariesLibraryAlbumsAlbum
